/**
 * Button Component - The Intelligent Standard
 *
 * Refined button with sophisticated interactions.
 * Uses the arena animation system for consistent motion.
 */

import { ButtonHTMLAttributes, ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;

  // Size configurations
  const sizeConfig = {
    small: { padding: '8px 16px', fontSize: '13px', height: '36px' },
    medium: { padding: '12px 24px', fontSize: '14px', height: '44px' },
    large: { padding: '16px 32px', fontSize: '16px', height: '52px' },
  };

  // Variant configurations
  const variantConfig = {
    primary: {
      background: '#8B2332',
      color: '#FFFFFF',
      border: 'none',
      hoverBg: '#6B1A27',
      disabledBg: '#E8E0D8',
      disabledColor: '#A0A0A0',
    },
    secondary: {
      background: '#FFFFFF',
      color: '#1A1A1A',
      border: '1px solid #E8E0D8',
      hoverBg: '#F5F0EB',
      disabledBg: '#F5F0EB',
      disabledColor: '#A0A0A0',
    },
    ghost: {
      background: 'transparent',
      color: '#1A1A1A',
      border: 'none',
      hoverBg: 'rgba(139, 35, 50, 0.05)',
      disabledBg: 'transparent',
      disabledColor: '#A0A0A0',
    },
    danger: {
      background: '#C41E3A',
      color: '#FFFFFF',
      border: 'none',
      hoverBg: '#A61B32',
      disabledBg: '#E8E0D8',
      disabledColor: '#A0A0A0',
    },
  };

  const s = sizeConfig[size];
  const v = variantConfig[variant];

  return (
    <button
      className={`
        btn-arena
        inline-flex items-center justify-center gap-2
        font-medium tracking-wide rounded-lg
        transition-all duration-200
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      style={{
        padding: s.padding,
        fontSize: s.fontSize,
        minHeight: s.height,
        background: isDisabled ? v.disabledBg : v.background,
        color: isDisabled ? v.disabledColor : v.color,
        border: v.border,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
      }}
      disabled={isDisabled}
      onMouseEnter={e => {
        if (!isDisabled) {
          e.currentTarget.style.background = v.hoverBg;
        }
      }}
      onMouseLeave={e => {
        if (!isDisabled) {
          e.currentTarget.style.background = v.background;
        }
      }}
      {...props}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <span className="flex gap-1">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: 'currentColor',
                  animation: `arena-pulse 1s ease-in-out infinite`,
                  animationDelay: `${i * 150}ms`,
                }}
              />
            ))}
          </span>
          <span>Loading...</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
}
